import {
  Breadcrumbs,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageContainer,
  Skeleton,
  useApiClient,
} from '@startupquickstart/frontend';
import { Smartphone, ExternalLink, Building2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { OrganizationSwitcher } from '@clerk/clerk-react';

interface Organization {
  id: string;
  name: string;
  slug: string | null;
  description: string | null;
  imageUrl: string | null;
  isInternal: boolean;
  role: string;
  permissions: string[];
}

interface UserMeResponse {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  clerkId: string | null;
  imageUrl: string | null;
  lastSyncedAt: string | null;
  organizations: Organization[];
}

export function PersonalAccountHome() {
  const breadcrumbs = [{ label: 'Home', current: true }];
  const apiClient = useApiClient();

  // Fetch user data to check if they have organizations they can switch to
  const { data, isLoading } = useQuery({
    queryKey: ['User', 'me'],
    queryFn: async () => {
      return apiClient.get<{ success: boolean; data: UserMeResponse }>('/users/me');
    },
  });

  const userData = data?.data;
  const hasAvailableOrganizations = (userData?.organizations?.length ?? 0) > 0;

  // Placeholder URLs - update these with actual app store links
  const APP_STORE_URL = 'https://apps.apple.com/app/your-app-id';
  const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=your.app.id';

  return (
    <PageContainer>
      <Breadcrumbs items={breadcrumbs} className="mb-4" />

      <div className="flex flex-col items-center justify-center min-h-[60vh] max-w-2xl mx-auto">
        <Card className="w-full">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
                <Smartphone className="w-10 h-10 text-primary" />
              </div>
            </div>
            <CardTitle className="text-3xl">Welcome!</CardTitle>
            <CardDescription className="text-base mt-2">
              The web portal is currently not available for personal accounts
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            {isLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            ) : hasAvailableOrganizations ? (
              <>
                <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="text-left flex-1">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                        Organization Access Available
                      </p>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mb-4">
                        You have access to organizations. Please select one to access the web
                        portal:
                      </p>
                      <div className="flex justify-center">
                        <OrganizationSwitcher
                          appearance={{
                            elements: {
                              rootBox: 'mx-auto',
                            },
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-sm text-muted-foreground mb-2">
                    Or access your content via our mobile app:
                  </p>
                </div>
              </>
            ) : (
              <p className="text-muted-foreground">
                To access destination information and tour details, please download and use our
                mobile app.
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-black/90 transition-colors"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                <div className="text-left">
                  <div className="text-xs">Download on the</div>
                  <div className="text-sm font-semibold">App Store</div>
                </div>
                <ExternalLink className="w-4 h-4 ml-1" />
              </a>

              <a
                href={GOOGLE_PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-black/90 transition-colors"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                </svg>
                <div className="text-left">
                  <div className="text-xs">Get it on</div>
                  <div className="text-sm font-semibold">Google Play</div>
                </div>
                <ExternalLink className="w-4 h-4 ml-1" />
              </a>
            </div>

            {!hasAvailableOrganizations && (
              <div className="pt-6 border-t">
                <p className="text-sm text-muted-foreground">
                  If you believe you should have access to the web portal, please contact your
                  organization administrator.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
